import { useState } from 'react';
import { Pagination, Box, Drawer, Grid } from 'components/ui';
import { useQuery } from '@apollo/client/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFilter, initInspectionFilterValues } from 'contexts/filter';
import { usePaginate } from 'contexts/paginate';
import { DividerContainer } from 'components/styles_components/container';
import { useError } from 'contexts/error';
import FilterForm from 'components/inspection/filter_form';
import FilterTags from 'components/filter/tags';
import FilterButton from 'components/inspection/button/filter';
import CreateButton from 'components/inspection/button/create';
import FlexibleInput from 'components/flexible_input';
import InspectionQuery from 'queries/inspection/index';
import Inspections from 'components/inspection/index';
import { TitleTypography, SubtitleTypography } from 'components/styles_components/typography';

const IndexContainer = ({ placeId, inspectionSubjectId, showTitle = true }) => {
  const intl = useIntl();
  const { page, onPageChange } = usePaginate();
  const { inspectionsFilter, setInspectionsFilter } = useFilter();
  const [filterVisible, setFilterVisible] = useState(false);
  const [search, setSearch] = useState(null);
  const [sortBy, setSortBy] = useState('lifecycle_asc');
  const setError = useError();
  const {
    data = {
      space: { overInspectionsLimit: false, adminRole: true },
      inspections: { inspections: [], inspectionsTotal: 0, inspectionsTotalPages: 0 },
    },
    loading,
  } = useQuery(InspectionQuery, {
    variables: {
      ...inspectionsFilter,
      search: search,
      page: page,
      placeId: placeId ?? inspectionsFilter.placeId,
      inspectionSubjectId: inspectionSubjectId,
      sortBy: sortBy,
    },
    fetchPolicy: 'cache-and-network',
    onError: () => {
      setError('error.inspection.index');
    },
  });

  return (
    <>
      {showTitle && (
        <>
          <TitleTypography variant="h1">
            <FormattedMessage id="navbar.inspections" />
          </TitleTypography>

          <SubtitleTypography variant="h2">
            <FormattedMessage
              id="inspection.total"
              values={{ count: data.inspections.inspectionsTotal ?? 0 }}
            />
          </SubtitleTypography>
        </>
      )}
      {filterVisible && (
        <Drawer
          anchor="right"
          open={filterVisible}
          onClose={() => {
            setFilterVisible(false);
          }}
        >
          <FilterForm
            initialValues={inspectionsFilter}
            defaultValues={initInspectionFilterValues}
            placeId={placeId}
            onClose={() => {
              setFilterVisible(false);
            }}
            onSubmit={(values) => {
              onPageChange(1);
              setFilterVisible(false);
              setInspectionsFilter(values);
            }}
          />
        </Drawer>
      )}
      <Grid container spacing={1} alignItems="center" justifyContent="flex-end">
        <Grid size={{ xs: 12, sm: 'auto' }} sx={{ flexGrow: { sm: 1 }, minWidth: 0 }}>
          <FlexibleInput
            placeholder={intl.formatMessage({ id: 'flexible_filter.inspection' })}
            value={search}
            onChange={setSearch}
          />
        </Grid>
        <Grid size="auto" sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <FilterButton
            onClick={() => setFilterVisible(!filterVisible)}
            initInspectionFilterValues={initInspectionFilterValues}
            inspectionsFilter={inspectionsFilter}
          />
          {data.space.adminRole && (
            <CreateButton
              placeId={placeId}
              inspectionSubjectId={inspectionSubjectId}
              overInspectionsLimit={data.space.overInspectionsLimit}
            />
          )}
        </Grid>
      </Grid>
      <FilterTags
        filterValues={placeId ? { ...inspectionsFilter, placeId: null } : inspectionsFilter}
        defaultValues={initInspectionFilterValues}
        onFilterChange={(newFilterValues) => {
          onPageChange(1);
          setInspectionsFilter(newFilterValues);
        }}
      />
      <Inspections
        loading={loading}
        inspections={data.inspections}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />
      <Box display="flex" justifyContent="center">
        {data.inspections.inspectionsTotalPages > 1 && (
          <Pagination
            count={data.inspections.inspectionsTotalPages}
            page={page}
            onChange={(_e, nextPage) => {
              onPageChange(nextPage);
            }}
          />
        )}
      </Box>
      <DividerContainer />
    </>
  );
};

export default IndexContainer;
